import React, { useState } from 'react'
import type {
	SelectTabData,
	SelectTabEvent
} from '@fluentui/react-components'
import {
	Card,
	Text,
	Tab,
	TabList,
	makeStyles,
	tokens
} from '@fluentui/react-components'
import { DocumentLibraryViewer } from './DocumentLibraryViewer'
import { DynamicForm } from './DynamicForm'
import { spfxShowcaseContent } from './content'
import { CodeRegular, DocumentMultipleRegular, FormRegular, ShareRegular } from '@fluentui/react-icons'
import { useCommonStyles } from '../common/styles'

const useStyles = makeStyles({
	featureGrid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
		gap: '1rem',
		marginBottom: '2rem'
	},
	techStack: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '0.5rem',
		marginTop: '1rem'
	},
	techBadge: {
		padding: '0.25rem 0.5rem',
		backgroundColor: tokens.colorNeutralBackground2,
		borderRadius: tokens.borderRadiusSmall,
		fontSize: '0.75rem',
		color: tokens.colorNeutralForeground2
	}
})

type TabValue = 'overview' | 'library' | 'form'
export const SpfxShowcase: React.FC = () => {
	const styles = useStyles()
	const commonStyles = useCommonStyles()
	const [selectedTab, setSelectedTab] = useState<TabValue>('overview')

	const onTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
		setSelectedTab(data.value as TabValue)
	}

	const renderOverview = () => (
		<div>
			<Card style={{ marginBottom: '2rem', padding: '2rem' }}>
				<Text size={500} weight="semibold" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
					<ShareRegular style={{ marginRight: '0.5rem', color: tokens.colorPaletteBlueBorderActive }} />
					{spfxShowcaseContent.overview.title}
				</Text>
				<Text style={{ marginBottom: '1rem' }}>
					{spfxShowcaseContent.overview.description}
				</Text>
				<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
					{spfxShowcaseContent.overview.capabilities.map((capability, index) => (
						<div key={index} style={{ display: 'flex', alignItems: 'center' }}>
							<CodeRegular style={{ marginRight: '0.5rem', color: tokens.colorPaletteGreenBorderActive }} />
							<Text size={300}>{capability}</Text>
						</div>
					))}
				</div>
			</Card>

			<div className={styles.featureGrid}>
				{spfxShowcaseContent.features.map((feature, index) => (
					<Card key={index} className={commonStyles.featureCard} onClick={() => setSelectedTab(feature.tab as TabValue)}>
						<div>
							{feature.tab === 'library'
								? <DocumentMultipleRegular style={{ fontSize: '2rem', color: tokens.colorPaletteBlueBorderActive }} />
								: <FormRegular style={{ fontSize: '2rem', color: tokens.colorPaletteGreenBorderActive }} />
							}
							<Text size={500} weight="semibold" style={{ margin: '1rem 0 0.5rem 0' }}>
								{feature.title}
							</Text><br />
							<Text size={300}>{feature.description}</Text>
						</div>
						<div className={styles.techStack}>
							{feature.technologies.map((tech, techIndex) => (
								<span key={techIndex} className={styles.techBadge}>
									{tech}
								</span>
							))}
						</div>
					</Card>
				))}
			</div>
		</div>
	)

	return (
		<div className={commonStyles.container}>
			<div className={commonStyles.tabContainer}>
				<TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
					<Tab value="overview">Overview</Tab>
					<Tab value="library">Document Library</Tab>
					<Tab value="form">Dynamic Form</Tab>
				</TabList>

				<div className={commonStyles.tabContent}>
					{selectedTab === 'overview' && renderOverview()}
					{selectedTab === 'library' && <DocumentLibraryViewer />}
					{selectedTab === 'form' && <DynamicForm />}
				</div>
			</div>
		</div >
	)
}

export default SpfxShowcase